import { Component, Injector, OnInit } from '@angular/core';
import { NotificationType } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { setMfaFilter } from '../../../../../../../aumico-lib-cpmm-fe/src/lib/actions/ui.actions';
import { SortDirection } from '../../../../../../../aumico-lib-cpmm-fe/src/lib/enums/sort-direction.enum';
import { selectMfaFilter } from '../../../../../../../aumico-lib-cpmm-fe/src/lib/reducers/ui.reducer';
import { MfaSortingProperties } from '../../../../../../../aumico-lib-users-fe/src/lib/enums/mfa-sorting-properties.enum';
import { MfaService, MfaUserStatus } from '../../../../../../../aumico-lib-users-fe/src/lib/services/mfa.service';
import { BasePageComponent } from '../../../../pages/base.page';

@UntilDestroy()
@Component({
    selector: 'app-page-organization-2fa',
    templateUrl: './organization-2fa.page.html',
    styleUrls: ['./organization-2fa.page.scss'],
})
export class Organization2faPage extends BasePageComponent implements OnInit {
    mfaEnabled = false;
    /** Until the org status is known neither state is shown — otherwise the "disabled" view flashes on every visit. */
    isLoadingStatus = true;
    users: MfaUserStatus[] = [];
    isLoadingToggle = false;
    isLoadingUsers = false;

    activeSortBy: MfaSortingProperties | null = null;
    activeSortOrder: SortDirection | null = null;

    readonly MfaSortingProperties = MfaSortingProperties;
    readonly SortDirection = SortDirection;

    constructor(injector: Injector, private mfaService: MfaService, private store: Store) {
        super(injector);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        // Restore sort state from the store
        this.store
            .select(selectMfaFilter())
            .pipe(untilDestroyed(this))
            .subscribe((filter) => {
                this.activeSortBy = filter.sortBy ?? null;
                this.activeSortOrder = filter.sortOrder ?? null;
            });

        this.loadMfaStatus();
    }

    private loadMfaStatus(): void {
        this.mfaService
            .getOrgMfaStatus$()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (status) => {
                    this.mfaEnabled = status.isEnabled;
                    this.isLoadingStatus = false;
                    if (this.mfaEnabled) {
                        this.loadUsers();
                    }
                },
                error: () => {
                    // keep mfaEnabled = false if the status call fails
                    this.isLoadingStatus = false;
                },
            });
    }

    private loadUsers(silent = false): void {
        if (!silent) {
            this.isLoadingUsers = true;
        }
        this.mfaService
            .getUsersMfaStatus$({
                sortBy: this.activeSortBy ?? undefined,
                sortOrder: this.activeSortOrder ?? undefined,
            })
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (users) => {
                    console.log('Users MFA status received', users);
                    this.users = cloneDeep<MfaUserStatus[]>(users);
                    this.isLoadingUsers = false;
                },
                error: () => {
                    this.isLoadingUsers = false;
                },
            });
    }

    onMfaToggleChange(enabled: boolean): void {
        if (enabled) {
            this.enableMfa();
        } else {
            this.disableMfa();
        }
    }

    enableMfa(): void {
        this.isLoadingToggle = true;
        this.mfaService
            .enableMfa$()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.mfaEnabled = true;
                    this.isLoadingToggle = false;
                    this.loadUsers();
                    this.aumicoCoreProvider.dispatchAddNotification(
                        'aumico_mfa_org_notification_success_title',
                        'aumico_mfa_org_enable_success',
                        NotificationType.SUCCESS,
                        3000,
                    );
                },
                error: () => {
                    this.isLoadingToggle = false;
                    this.aumicoCoreProvider.dispatchAddNotification(
                        'aumico_mfa_org_notification_error_title',
                        'aumico_mfa_org_enable_error',
                        NotificationType.ERROR,
                        3000,
                    );
                },
            });
    }

    disableMfa(): void {
        this.isLoadingToggle = true;
        this.mfaService
            .disableMfa$()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.mfaEnabled = false;
                    this.isLoadingToggle = false;
                    this.aumicoCoreProvider.dispatchAddNotification(
                        'aumico_mfa_org_notification_success_title',
                        'aumico_mfa_org_disable_success',
                        NotificationType.SUCCESS,
                        3000,
                    );
                },
                error: () => {
                    this.isLoadingToggle = false;
                    this.aumicoCoreProvider.dispatchAddNotification(
                        'aumico_mfa_org_notification_error_title',
                        'aumico_mfa_org_disable_error',
                        NotificationType.ERROR,
                        3000,
                    );
                },
            });
    }

    resetUserMfa(userId: string): void {
        this.mfaService
            .resetUserMfa$(userId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.aumicoCoreProvider.dispatchAddNotification(
                        'aumico_mfa_org_notification_success_title',
                        'aumico_mfa_org_reset_user_success',
                        NotificationType.SUCCESS,
                        3000,
                    );
                    // Optimistically update the user's MFA state so the table
                    // reflects the reset immediately without waiting for the reload.
                    this.users = cloneDeep<MfaUserStatus[]>(this.users.map((u) =>
                        u.userId === userId ? { ...u, isMfaSetupComplete: false, enabledAt: undefined } : u,
                    ));
                    // Silent background refresh — no loading flicker.
                    this.loadUsers(true);
                },
                error: () => {
                    this.aumicoCoreProvider.dispatchAddNotification(
                        'aumico_mfa_org_notification_error_title',
                        'aumico_mfa_org_reset_user_error',
                        NotificationType.ERROR,
                        3000,
                    );
                },
            });
    }

    sortBy(sortByProperty: MfaSortingProperties): void {
        let newSortOrder = SortDirection.ASC;

        if (this.activeSortBy === sortByProperty) {
            newSortOrder = this.activeSortOrder === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
        }

        this.activeSortBy = sortByProperty;
        this.activeSortOrder = newSortOrder;

        this.store.dispatch(setMfaFilter({ filter: { sortBy: sortByProperty, sortOrder: newSortOrder } }));

        if (this.mfaEnabled) {
            this.loadUsers();
        }
    }

    formatEnabledAt(dateStr: string): string {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} - ${hours}:${minutes}`;
    }
}
